import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { revealDictationCaret } from './dictationCaret';
import MicRounded from '@mui/icons-material/MicRounded';
import StopRounded from '@mui/icons-material/StopRounded';
import { Tooltip } from '@mui/joy';
import './voice-input.css';

let activeSession = null;
const field = ref => {
  const el = ref?.current;
  return el?.matches?.('input,textarea,[contenteditable="true"]') ? el : el?.querySelector?.('input,textarea,[contenteditable="true"]');
};
export function stopDictationFor(target) {
  if (activeSession?.target === target) activeSession.stop();
}
export function deferDictationBlur(event) {
  const target = event.target;
  // Keep the editor alive for its mic controls or browser permission UI.
  // Ordinary blur must still commit the current live text, just like typing.
  return !!event.relatedTarget?.closest?.('[data-dictation-control]') || (target?.dataset?.dictating === 'true' && !document.hasFocus());
}
const messages = {
  'not-allowed': 'Разрешите микрофон в настройках сайта и нажмите ещё раз.',
  'service-not-allowed': 'Распознавание речи запрещено браузером или настройками организации.',
  'audio-capture': 'Микрофон недоступен. Проверьте подключение и разрешения.',
  network: 'Нет связи с сервисом распознавания браузера. Проверьте интернет.',
  'no-speech': 'Речь не обнаружена. Нажмите микрофон и попробуйте ещё раз.',
  'language-not-supported': 'Этот браузер не поддерживает распознавание на русском языке.',
};

// Only the explicit mic click starts audio. Native SpeechRecognition may send
// audio to the browser vendor; LunaDesk does not upload or retain the recording.
export default function VoiceInput({ targetRef, onChange, onInput, maxLength, disabled = false, label = 'Голосовой ввод' }) {
  const [running, setRunning] = useState(false), [speaking, setSpeaking] = useState(false), [message, setMessage] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [chipPosition, setChipPosition] = useState(null);
  const session = useRef(null), callbacks = useRef(null), mounted = useRef(true);
  callbacks.current = { onChange, onInput, maxLength };
  function stop() { session.current?.stop(); }
  useEffect(() => {
    mounted.current = true;
    const hide = () => { if (document.hidden) session.current?.abort(); };
    const leave = () => session.current?.abort();
    const escape = e => { if (e.key === 'Escape') session.current?.abort(); };
    document.addEventListener('visibilitychange', hide); window.addEventListener('pagehide', leave); document.addEventListener('keydown', escape, true);
    return () => { mounted.current = false; session.current?.abort(); document.removeEventListener('visibilitychange', hide); window.removeEventListener('pagehide', leave); document.removeEventListener('keydown', escape, true); };
  }, []);
  useEffect(() => {
    if (!running) return;
    const started = Date.now(), tick = setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(tick);
  }, [running]);
  useEffect(() => {
    if (!running) { setChipPosition(null); return; }
    const position = () => {
      const target = session.current?.target;
      if (!target?.isConnected) return;
      const rect = target.getBoundingClientRect(), viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0, left = viewport?.offsetLeft || 0, width = viewport?.width || window.innerWidth;
      setChipPosition({ left: Math.max(left + 8, Math.min(rect.right - 202, left + width - 210)), top: rect.top - 53 >= top + 8 ? rect.top - 53 : rect.bottom + 8 });
    };
    position(); window.addEventListener('resize', position); document.addEventListener('scroll', position, true);
    window.visualViewport?.addEventListener('resize', position); window.visualViewport?.addEventListener('scroll', position);
    return () => { window.removeEventListener('resize', position); document.removeEventListener('scroll', position, true); window.visualViewport?.removeEventListener('resize', position); window.visualViewport?.removeEventListener('scroll', position); };
  }, [running, seconds]);
  function start() {
    setMessage('');
    const Engine = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Engine) { setMessage('Голосовой ввод недоступен в этом браузере. Попробуйте Chrome или Edge.'); return; }
    if (!window.isSecureContext) { setMessage('Для микрофона откройте LunaDesk по HTTPS.'); return; }
    const target = field(targetRef);
    if (!target || target.disabled || target.readOnly) return;
    activeSession?.abort();
    const rich = target.isContentEditable;
    target.focus();
    const initial = rich ? null : target.value;
    const from = rich ? 0 : (target.selectionStart ?? initial.length), to = rich ? 0 : (target.selectionEnd ?? from);
    let range, textNode = null, richLeft = '', richRight = '';
    if (rich) {
      const selection = window.getSelection();
      if (selection.rangeCount && target.contains(selection.getRangeAt(0).commonAncestorContainer)) range = selection.getRangeAt(0).cloneRange();
      else { range = document.createRange(); range.selectNodeContents(target); range.collapse(false); }
      const before = range.cloneRange(), after = range.cloneRange();
      before.selectNodeContents(target); before.setEnd(range.startContainer, range.startOffset);
      after.selectNodeContents(target); after.setStart(range.endContainer, range.endOffset);
      richLeft = /\S$/.test(before.toString()) ? ' ' : '';
      richRight = /^\S/.test(after.toString()) && !/^[.,!?;:)]/.test(after.toString()) ? ' ' : '';
    }
    let engine;
    try { engine = new Engine(); } catch { setMessage('Сервис распознавания недоступен в этом браузере.'); return; }
    engine.lang = 'ru-RU'; engine.continuous = true; engine.interimResults = true; engine.maxAlternatives = 1;
    let ended = false, ending = false, watchdog;
    const clean = () => {
      if (ended) return;
      ended = true; clearTimeout(watchdog); delete target.dataset.dictating;
      target.removeEventListener('input', manual); target.removeEventListener('blur', blur);
      if (session.current === handle) session.current = null;
      if (activeSession === handle) activeSession = null;
      if (mounted.current) { setRunning(false); setSpeaking(false); }
    };
    const handle = { target,
      stop() { if (ended || ending) return; ending = true; watchdog = setTimeout(() => { try { engine.abort(); } catch {} clean(); }, 1500); try { engine.stop(); } catch { clean(); } },
      abort() { if (ended) return; try { engine.abort(); } catch {} clean(); },
    };
    const manual = () => handle.abort(); // Never overwrite a manual edit with late results.
    const blur = e => { if (!e.relatedTarget?.closest?.('[data-dictation-control]')) handle.stop(); };
    engine.onresult = event => {
      if (ended || !target.isConnected) return;
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) transcript += event.results[i][0].transcript + ' ';
      transcript = transcript.trim();
      if (!transcript) return;
      if (rich) {
        if (!textNode) { range.deleteContents(); textNode = document.createTextNode(''); range.insertNode(textNode); }
        textNode.textContent = richLeft + transcript + richRight;
        if (document.activeElement === target) { const caret = document.createRange(); caret.setStartAfter(textNode); caret.collapse(true); const selection = window.getSelection(); selection.removeAllRanges(); selection.addRange(caret); }
        callbacks.current.onInput?.();
        requestAnimationFrame(() => { if (target.isConnected && document.activeElement === target) revealDictationCaret(target); });
      } else {
        const prefix = initial.slice(0, from), suffix = initial.slice(to);
        const left = prefix && !/\s$/.test(prefix) ? ' ' : '', right = suffix && !/^\s|^[.,!?;:)]/.test(suffix) ? ' ' : '';
        const room = (callbacks.current.maxLength ?? target.maxLength) > 0 ? Math.max(0, (callbacks.current.maxLength ?? target.maxLength) - prefix.length - suffix.length - left.length - right.length) : Infinity;
        const value = prefix + left + transcript.slice(0, room) + right + suffix;
        callbacks.current.onChange?.(value);
        requestAnimationFrame(() => { if (target.isConnected && target.value === value && document.activeElement === target) { const caret = Math.min(value.length, prefix.length + left.length + transcript.slice(0, room).length); revealDictationCaret(target, caret); } });
      }
    };
    engine.onspeechstart = () => { if (mounted.current && !ended) setSpeaking(true); };
    engine.onspeechend = () => { if (mounted.current && !ended) setSpeaking(false); };
    engine.onerror = e => { if (ended) return; if (mounted.current && e.error !== 'aborted') setMessage(messages[e.error] || 'Не удалось распознать речь. Попробуйте ещё раз.'); clean(); try { engine.abort(); } catch {} };
    engine.onend = clean;
    session.current = handle; activeSession = handle; target.dataset.dictating = 'true';
    target.addEventListener('input', manual); target.addEventListener('blur', blur);
    setSeconds(0); setRunning(true);
    try { engine.start(); } catch { clean(); try { engine.abort(); } catch {} setMessage('Не удалось включить микрофон. Проверьте разрешения браузера.'); }
  }
  const hint = running ? 'Остановить диктовку. Текст уже находится в поле.' : 'Диктовка на русском. Нужны HTTPS, микрофон и поддержка браузера. Аудио может обрабатываться сервисом браузера.';
  return <span className={'ld-voice ' + (running ? 'is-running' : '') + (speaking ? ' is-speaking' : '')} data-dictation-control data-action="dictation">
    <Tooltip title={hint}><button type="button" disabled={disabled} className="ld-voice-button" data-dictation-control data-action="dictation" aria-label={running ? 'Остановить диктовку' : label} aria-pressed={running} onPointerDown={e => e.preventDefault()} onMouseDown={e => e.preventDefault()} onClick={e => { e.stopPropagation(); if (running) stop(); else start(); }}>
      <MicRounded sx={{ fontSize: 18 }}/></button></Tooltip>
    {running && chipPosition && createPortal(<div className={'ld-voice-chip' + (speaking ? ' is-speaking' : '')} style={chipPosition} data-dictation-control onClick={e => e.stopPropagation()} onPointerDown={e => { e.preventDefault(); e.stopPropagation(); }}><span className="ld-voice-record-dot"/><span className="ld-voice-bars" aria-hidden="true">{[0,1,2,3,4].map(i => <i key={i}/>)}</span><span className="ld-voice-time" role="timer" aria-label="Диктовка идёт">{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}</span><button type="button" aria-label="Остановить диктовку" onClick={stop}><StopRounded sx={{ fontSize: 16 }}/><span>Стоп</span></button></div>, document.body)}
    {message && <span className="ld-voice-message" role="alert" onClick={e => e.stopPropagation()}>{message}<button type="button" aria-label="Закрыть подсказку диктовки" onMouseDown={e => e.preventDefault()} onClick={() => setMessage('')}>×</button></span>}
  </span>;
}
